import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Card, Select, Table, Button, Space, Modal, Form, Input, Switch, message, Popconfirm, Typography, Tag, Empty, Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, StarOutlined, StarFilled, CopyOutlined } from '@ant-design/icons';
import registrarService from '../../services/registrarService';
import PageHeader from '../../../shared/components/PageHeader';
import CountryFlag from '../../../shared/components/CountryFlag';
import ContactFields, { fieldErrorsToForm, fromFormContact, toFormContact } from './components/ContactFields';

const { Text } = Typography;

const normalizeFieldDefs = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') return Object.entries(data).map(([key, field]) => ({ name: key, ...field }));
    return [];
};

const optionsOf = (opts) => {
    if (Array.isArray(opts)) {
        return opts.map((o) => (typeof o === 'object' ? { value: o.value, label: o.label ?? String(o.value) } : { value: o, label: String(o) }));
    }
    return Object.entries(opts || {}).map(([value, label]) => ({ value, label: typeof label === 'object' ? (label.label ?? value) : String(label) }));
};

/**
 * 通讯录（联系人资料）：只用于注册/转入/编辑域名时预填。
 * 修改这里不会改动任何已注册的域名（每个域名有自己的一份联系人）。
 */
const ContactTemplates = () => {
    const { t, i18n } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    // TLD 身份资料（如 .us 用途、.cn 证件）
    const [extForm] = Form.useForm();
    const [extTarget, setExtTarget] = useState(null);
    const [extTld, setExtTld] = useState('');
    const [fieldDefs, setFieldDefs] = useState([]);
    const [defLoading, setDefLoading] = useState(false);
    const [supportedTlds, setSupportedTlds] = useState([]);
    const [groupedTlds, setGroupedTlds] = useState({});

    useEffect(() => {
        loadTemplates();
        registrarService.getSupportedTldsWithFields().then((r) => {
            if (r?.success) {
                const raw = r.data;
                setSupportedTlds(Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []));
                setGroupedTlds(r.grouped || {});
            }
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const r = await registrarService.getContactTemplates();
            if (r.success) setTemplates(Array.isArray(r.data) ? r.data : []);
            else message.error(r.error || t('Failed to load'));
        } catch (e) {
            message.error(e.error || e.message || t('Failed to load'));
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ ...toFormContact({}), template_name: '', is_default: templates.length === 0 });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.resetFields();
        form.setFieldsValue({ ...toFormContact(record), template_name: record.template_name, is_default: !!record.is_default });
        setModalOpen(true);
    };

    const submit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            return;
        }
        const payload = {
            ...fromFormContact(values),
            template_name: (values.template_name || '').trim() || `${values.first_name} ${values.last_name}`.trim(),
            is_default: !!values.is_default,
        };
        setSaving(true);
        try {
            const r = editing
                ? await registrarService.updateContactTemplate({ id: editing.id, ...payload })
                : await registrarService.createContactTemplate(payload);
            if (r.success) {
                message.success(t('Saved'));
                setModalOpen(false);
                loadTemplates();
            } else {
                message.error(r.error || t('Failed to save'));
            }
        } catch (e) {
            const fe = fieldErrorsToForm(e?.field_errors, () => [], { t, lang: i18n?.language, form });
            if (fe.length) form.setFields(fe);
            message.error(e?.error || e?.message || t('Failed to save'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            const r = await registrarService.deleteContactTemplate(id);
            if (r.success) { message.success(t('Deleted')); loadTemplates(); } else message.error(r.error || t('Deletion failed'));
        } catch (e) {
            message.error(e.error || e.message || t('Deletion failed'));
        }
    };

    const setDefault = async (id) => {
        try {
            const r = await registrarService.setDefaultContactTemplate(id);
            if (r.success) loadTemplates(); else message.error(r.error || t('Failed to save settings'));
        } catch (e) {
            message.error(e.error || e.message || t('Failed to save settings'));
        }
    };

    const duplicate = async (record) => {
        try {
            const r = await registrarService.createContactTemplate({
                ...fromFormContact(toFormContact(record)),
                template_name: t('{{name}} (copy)', { name: record.template_name }),
                is_default: false,
                extension_fields: record.extension_fields || {},
            });
            if (r.success) { message.success(t('Copied')); loadTemplates(); } else message.error(r.error || t('Failed to copy'));
        } catch (e) {
            message.error(e.error || e.message || t('Failed to copy'));
        }
    };

    // ---------- TLD identity fields ----------
    const openExt = (record) => {
        setExtTarget(record);
        setFieldDefs([]);
        setExtTld('');
        extForm.resetFields();
    };

    const changeExtTld = async (val) => {
        const tld = (val || '').replace(/^\./, '').toLowerCase();
        setExtTld(tld);
        setFieldDefs([]);
        if (!tld) return;
        setDefLoading(true);
        try {
            const r = await registrarService.getAdditionalFields(tld);
            const defs = r?.success ? normalizeFieldDefs(r.data) : [];
            setFieldDefs(defs);
            const ext = (extTarget?.extension_fields || {})[tld] || {};
            const init = {};
            defs.forEach((d) => {
                const key = d.scope === 'contact_extension' && d.contact_field_mapping ? d.contact_field_mapping : (d.standard_name || d.name);
                if (ext[key] !== undefined) init[d.name] = ext[key];
                else if (ext[d.name] !== undefined) init[d.name] = ext[d.name];
            });
            extForm.setFieldsValue(init);
        } catch (_) {
            setFieldDefs([]);
        } finally {
            setDefLoading(false);
        }
    };

    const saveExt = async () => {
        if (!extTld || fieldDefs.length === 0) { setExtTarget(null); return; }
        let values;
        try { values = await extForm.validateFields(); } catch (_) { return; }
        const fields = {};
        fieldDefs.forEach((d) => {
            const val = values[d.name];
            if (val === undefined || val === null || val === '') return;
            const key = d.scope === 'contact_extension' && d.contact_field_mapping ? d.contact_field_mapping : (d.standard_name || d.name);
            fields[key] = val;
        });
        const merged = { ...(extTarget.extension_fields || {}), [extTld]: fields };
        try {
            const r = await registrarService.updateContactTemplate({ id: extTarget.id, extension_fields: merged });
            if (r.success) { message.success(t('Saved')); setExtTarget(null); loadTemplates(); } else message.error(r.error || t('Failed to save'));
        } catch (e) {
            message.error(e.error || e.message || t('Failed to save'));
        }
    };

    const columns = [
        {
            title: t('Name'),
            dataIndex: 'template_name',
            render: (name, r) => (
                <Space size={6}>
                    <span style={{ fontWeight: 500 }}>{name}</span>
                    {r.is_default && <Tag color="gold" icon={<StarFilled />}>{t('Default')}</Tag>}
                </Space>
            ),
        },
        {
            title: t('Contacts'),
            key: 'name',
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span>{`${r.first_name || ''} ${r.last_name || ''}`.trim()}</span>
                    {r.kind === 'organization' && r.company && <Text type="secondary" style={{ fontSize: 12 }}>{r.company}</Text>}
                </Space>
            ),
        },
        { title: t('Email'), dataIndex: 'email', render: (v) => v || '-' },
        { title: t('Phone'), dataIndex: 'phone' },
        {
            title: t('Address'),
            key: 'address',
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span>{[r.address, r.address2].filter(Boolean).join(', ')}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}><CountryFlag code={r.country} />{[r.city, r.state, r.postal_code, r.country].filter(Boolean).join(', ')}</Text>
                </Space>
            ),
        },
        {
            title: t('Extension info'),
            key: 'ext',
            render: (_, r) => {
                const tlds = Object.keys(r.extension_fields || {});
                return tlds.length ? <Space size={[4, 4]} wrap>{tlds.map((x) => <Tag key={x}>.{x}</Tag>)}</Space> : <Text type="secondary">-</Text>;
            },
        },
        {
            title: '',
            key: 'action',
            width: 190,
            render: (_, r) => (
                <Space size={2} wrap>
                    <Tooltip title={r.is_default ? t('Default') : t('Set as default')}>
                        <Button type="text" size="small" icon={r.is_default ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} disabled={r.is_default} onClick={() => setDefault(r.id)} />
                    </Tooltip>
                    <Tooltip title={t('Edit')}><Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
                    <Tooltip title={t('Copy')}><Button type="text" size="small" icon={<CopyOutlined />} onClick={() => duplicate(r)} /></Tooltip>
                    <Tooltip title={t('Extra info some extensions (e.g. .us .cn) require')}>
                        <Button type="text" size="small" onClick={() => openExt(r)}>{t('Extension info')}</Button>
                    </Tooltip>
                    <Popconfirm title={t('Delete this entry? Registered domains are not affected')} onConfirm={() => remove(r.id)} okText={t('Delete')} cancelText={t('Cancel')}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <PageHeader
                title={t('Address book')}
                subtitle={t('Used to fill in domain registrations; editing here doesn\'t change registered domains')}
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('Create')}</Button>}
            />

            <Card bordered={false}>
                <Table
                    columns={columns}
                    dataSource={templates}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No contacts yet')}>
                                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('Create')}</Button>
                            </Empty>
                        ),
                    }}
                />
            </Card>

            <Modal
                title={editing ? t('Edit contact') : t('New contact')}
                open={modalOpen}
                onOk={submit}
                onCancel={() => setModalOpen(false)}
                confirmLoading={saving}
                okText={t('Save')}
                cancelText={t('Cancel')}
                width={640}
                forceRender
            >
                <Form form={form} layout="vertical" requiredMark={false}>
                    <Form.Item name="template_name" label={t('Name')} tooltip={t('For your reference, e.g. "Personal" or "Company"')}>
                        <Input placeholder={t('Leave empty to use the name')} maxLength={100} />
                    </Form.Item>
                    <ContactFields />
                    <Form.Item name="is_default" valuePropName="checked" label={t('Set as default')} style={{ marginBottom: 0 }}>
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t('Extension info · {{name}}', { name: extTarget?.template_name || '' })}
                open={!!extTarget}
                onOk={saveExt}
                onCancel={() => setExtTarget(null)}
                okText={t('Save')}
                cancelText={t('Cancel')}
                width={600}
                forceRender
            >
                <Form form={extForm} layout="vertical">
                    <Form.Item label={t('Suffix')}>
                        <Select
                            placeholder={t('Select extension')}
                            showSearch
                            allowClear
                            value={extTld || undefined}
                            optionFilterProp="label"
                            onChange={changeExtTld}
                            loading={defLoading}
                            options={Object.entries(groupedTlds).map(([region, tlds]) => ({
                                label: t(region),
                                options: tlds.filter((x) => supportedTlds.includes(x)).map((x) => ({
                                    value: x,
                                    label: (extTarget?.extension_fields || {})[x] ? `.${x} ✓` : `.${x}`,
                                })),
                            }))}
                        />
                    </Form.Item>
                    {fieldDefs.map((d) => (
                        <Form.Item
                            key={d.name}
                            name={d.name}
                            label={t(d.label || d.name)}
                            rules={d.required ? [{ required: true, message: t('Required') }] : []}
                            tooltip={d.hint ? t(d.hint) : undefined}
                            valuePropName={d.type === 'checkbox' || d.type === 'boolean' ? 'checked' : 'value'}
                        >
                            {d.type === 'select' ? <Select options={optionsOf(d.options).map((o) => ({ ...o, label: t(o.label) }))} />
                                : (d.type === 'checkbox' || d.type === 'boolean') ? <Switch />
                                    : <Input />}
                        </Form.Item>
                    ))}
                    {!defLoading && extTld && fieldDefs.length === 0 && <Text type="secondary">{t('No extra info needed for this extension')}</Text>}
                </Form>
            </Modal>
        </div>
    );
};

export default ContactTemplates;
